import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../utils/supabase';

interface LogoSettings {
  logo_url: string;
  logo_alt_text: string;
  show_logo_image: boolean;
  logo_width: string;
  logo_height: string;
}

const defaultLogoSettings: LogoSettings = {
  logo_url: '',
  logo_alt_text: 'NWLC Logo',
  show_logo_image: false,
  logo_width: '120',
  logo_height: '120'
};

export const useLogoSettings = () => {
  const [logoSettings, setLogoSettings] = useState<LogoSettings>(defaultLogoSettings);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLogoSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('settings')
        .select('*')
        .eq('category', 'header')
        .in('setting_key', ['logo_url', 'logo_alt_text', 'show_logo_image', 'logo_width', 'logo_height'])
        .eq('is_active', true);

      if (fetchError) {
        throw fetchError;
      }

      if (data && data.length > 0) {
        const settings: Partial<LogoSettings> = {};
        
        data.forEach((setting: any) => {
          if (setting.setting_key in defaultLogoSettings) {
            const value = setting.setting_value;
            // Convert string booleans to actual booleans
            if (setting.setting_type === 'boolean') {
              (settings as any)[setting.setting_key] = value === 'true';
            } else {
              (settings as any)[setting.setting_key] = value;
            }
          }
        });

        setLogoSettings({ ...defaultLogoSettings, ...settings });
      } else {
        setLogoSettings(defaultLogoSettings);
      }
    } catch (err) {
      console.error('Error fetching logo settings:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch logo settings');
      setLogoSettings(defaultLogoSettings);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateLogoSettings = useCallback(async (newSettings: Partial<LogoSettings>) => {
    try {
      setError(null);

      const updates = Object.entries(newSettings).map(([key, value]) => ({
        setting_key: key,
        setting_value: String(value),
        setting_type: typeof value === 'boolean' ? 'boolean' : 'string',
        category: 'header',
        description: `Logo ${key.replace(/_/g, ' ')}`,
        is_active: true,
        updated_at: new Date().toISOString()
      }));

      const { error: updateError } = await supabase
        .from('settings')
        .upsert(updates, { onConflict: 'setting_key' });

      if (updateError) {
        throw updateError;
      }

      // Update local state
      setLogoSettings(prev => ({ ...prev, ...newSettings }));
      
      return { success: true };
    } catch (err) {
      console.error('Error updating logo settings:', err);
      setError(err instanceof Error ? err.message : 'Failed to update logo settings');
      return { success: false, error: err instanceof Error ? err.message : 'Failed to update logo settings' };
    }
  }, []);

  useEffect(() => {
    fetchLogoSettings();
  }, [fetchLogoSettings]);

  return {
    logoSettings,
    loading,
    error,
    updateLogoSettings,
    refetch: fetchLogoSettings
  };
};